/**
 * AIDIA date detection.
 *
 * Scans free text for due dates so a reminder can be raised the day before.
 * e.g. "Council rates on 25-Aug-26" -> due 2026-08-25, alert 2026-08-24.
 */
const MONTHS = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
  january: 0, february: 1, march: 2, april: 3, june: 5, july: 6,
  august: 7, september: 8, october: 9, november: 10, december: 11,
};

// Normalise a possibly 2-digit year into a full year (26 -> 2026).
export function fullYear(y) {
  const n = parseInt(y, 10);
  if (n >= 100) return n;
  return 2000 + n; // assume this century for 2-digit years
}

export function makeDate(year, month, day) {
  const d = new Date(year, month, day);
  // guard against invalid rollovers (e.g. 31 Feb)
  if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
  return d;
}

// Pattern definitions. Each returns a Date or null from its regex match groups.
const patterns = [
  // 25-Aug-26 / 25 Aug 2026 / 25/Aug/26  (day - monthname - year)
  {
    re: /\b(\d{1,2})[\s\-\/]([A-Za-z]{3,9})[\s\-\/](\d{2,4})\b/g,
    build: (m) => {
      const mon = MONTHS[m[2].toLowerCase()];
      if (mon === undefined) return null;
      return makeDate(fullYear(m[3]), mon, parseInt(m[1], 10));
    },
  },
  // Aug 25, 2026 / August 25 2026  (monthname - day - year)
  {
    re: /\b([A-Za-z]{3,9})\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{2,4})\b/g,
    build: (m) => {
      const mon = MONTHS[m[1].toLowerCase()];
      if (mon === undefined) return null;
      return makeDate(fullYear(m[3]), mon, parseInt(m[2], 10));
    },
  },
  // 25/08/2026 or 25-08-26  (day/month/year, day-first as per AU locale)
  {
    re: /\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})\b/g,
    build: (m) => {
      const day = parseInt(m[1], 10);
      const mon = parseInt(m[2], 10) - 1;
      if (mon < 0 || mon > 11 || day < 1 || day > 31) return null;
      return makeDate(fullYear(m[3]), mon, day);
    },
  },
  // 2026-08-25  (ISO)
  {
    re: /\b(\d{4})-(\d{1,2})-(\d{1,2})\b/g,
    build: (m) => makeDate(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)),
  },
];

export const fmtDate = (d) =>
  d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });

// Convert a Date to a yyyy-mm-dd string suitable for <input type="date">.
export function toInputValue(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function dayBefore(d) {
  const x = new Date(d);
  x.setDate(x.getDate() - 1);
  return x;
}

/**
 * Detect all due dates in a text.
 * Returns array of { matchText, dueDate, alertDate } sorted by date, de-duplicated.
 */
export function detect(text) {
  if (!text) return [];
  const found = new Map(); // key: yyyy-mm-dd of due date
  for (const p of patterns) {
    p.re.lastIndex = 0;
    let m;
    while ((m = p.re.exec(text)) !== null) {
      const due = p.build(m);
      if (!due) continue;
      const key = toInputValue(due);
      if (!found.has(key)) {
        found.set(key, { matchText: m[0], dueDate: due, alertDate: dayBefore(due) });
      }
    }
  }
  return [...found.values()].sort((a, b) => a.dueDate - b.dueDate);
}
